// @ts-nocheck
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import TwoButtonModal from "@store/Overrides/Components/TwoButtonModal";
import { lazyLoadStyles } from "@store/Themes/common/mixins/lazyload";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { getWishListLines, GetWishListLinesApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import translate from "@insite/client-framework/Translate";
import { ProductDto, WishListLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import { DataTablePresentationProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import DeleteIcon from "@insite/mobius/Icons/X";
import {
    Checkbox,
    DataTable,
    DataTableCell,
    DataTableRow,
    GridContainer,
    GridItem,
    Icon,
    Link,
    Modal,
    Typography,
} from "@insite/mobius/index";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import Pagination from "@store/MobiusOverrides/Pagination/Pagination";
import useBuyingList from "@store/Framework/Common/Hooks/useBuyingList/useBuyingList";
import useIsMobile from "@store/Framework/Common/Hooks/useIsMobile";
import useSearch from "@store/Framework/Common/Hooks/useSearch";
import ManageModalUploadAccordian from "@store/Library/Components/Bunzl/Common/ManageModalUploadAccordian";
import SearchBox from "@store/Library/Components/Bunzl/Common/SearchBox";
import BuyingListTableHead from "@store/Library/Components/BuyingList/BuyingListTableHead";
import ProductModalSearch from "@store/Library/Components/BuyingList/ProductModalSearch";
import { StyledCol } from "@store/Library/Components/Common/StyledCol";
import FadeIn from "@store/Library/Components/Reporting/Common/FadeIn";
import Button from "@store/MobiusOverrides/Button/Button";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled, { css } from "styled-components";

interface ManageProductsModalProps {
    buyingList: WishListModel | null;
    isOpen: boolean;
    onClose: () => void;
}
export const StyledModal = styled(Modal)`
    div[role="dialog"] {
        min-width: 70vw;
        height: 80vh;
    }
`;
export const StyledRow = styled(DataTableRow)<{ isHighlighted: boolean }>`
    border-radius: 0.5rem;
    transition: all 0.3s ease-in-out;
    outline: 1px solid
        ${props =>
            props.isHighlighted
                ? css`
                      ${getColor("secondary")}
                  `
                : "transparent"};
`;
const mobileQuery = getMediaQuery({ option: "only", first: true, min: 0, max: 700, last: false });

const mobileColumnWidths = [70, 30];
const desktopColumnWidths = [5, 85, 10];
const tabletColumnWidths = [5, 75, 15];

const getColumnWidthsToUse = (isMobile: boolean | null, isTablet: boolean | null) => {
    if (isMobile) {
        return mobileColumnWidths;
    }
    if (isTablet) {
        return tabletColumnWidths;
    }

    return desktopColumnWidths;
};
const ManageProductsModal: React.FC<ManageProductsModalProps> = ({ buyingList, onClose, isOpen }) => {
    const dispatch = useDispatch();
    const selectedProductInfo = useAppSelector(state => state.components.productSelector.selectedProductInfo);
    const [selectedProducts, setSelectedProducts] = React.useState<string[]>([]);
    const { updateBuyingLists, loading } = useBuyingList();
    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

    const [wishListLines, setWishListLines] = useState<WishListLineCollectionModel | undefined>();
    interface ManageProductModalStyles {
        modal: ModalPresentationProps;
        dataTable: DataTablePresentationProps;
        dataTableNameCell: DataTableCellProps;
        gridItemCentered: GridItemProps;
        gridItemWarningBox: GridItemProps;
        deleteIcon: IconProps;
        infosBox: InfosBoxProps;
    }

    const styles: ManageProductModalStyles = {
        modal: {
            cssOverrides: {
                modalContent: css`
                    overflow: visible;
                    min-width: 50vw;
                    min-height: 50vh;
                `,
                modalBody: css`
                    overflow: auto;
                    min-width: 50vw;
                    min-height: 50vh;
                `,
            },
            headlineTypographyProps: {
                ellipsis: true,
                variant: "h3",
                css: css`
                    text-align: center;
                    max-width: 10rem;
                `,
            },
        },
        dataTable: {
            css: css`
                table-layout: fixed;
                td > * {
                    ${loading ? lazyLoadStyles() : undefined}
                }
            `,
        },
        gridItemCentered: {
            css: css`
                justify-content: center;
                align-items: center;
            `,
        },
        dataTableNameCell: {
            typographyProps: {
                ellipsis: true,
                css: css`
                    ${mobileQuery} {
                        > span {
                            white-space: normal;
                        }
                    }
                    width: 14rem;
                    display: block;
                `,
            },
        },
        deleteIcon: {
            css: css`
                color: ${getColor("primary")};
                cursor: pointer;
                ${loading ? "color: transparent;" : ""};
                & :hover {
                    color: ${getColor("secondary")};
                }
            `,
            size: 36,
        },
        gridItemWarningBox: {
            css: css`
                justify-content: center;
                align-items: center;
                background-color: ${getColor("warning.light")};
                padding-top: 2rem;
            `,
        },
        infosBox: {
            extendedStyles: {
                wrapper: {
                    css: css`
                        // !important is needed because some selector is overriding on the nth child
                        margin-bottom: 1rem !important;
                        width: 100%;
                    `,
                },
            },
            type: "warning",
        },
    };

    const [wishListLinesParameter, setWishListLinesParameter] = useState<GetWishListLinesApiParameter>({
        page: 1,
        wishListId: buyingList?.id || "",
    });
    const isMobile = useIsMobile();
    const isTablet = useIsMobile({ width: 1025 });
    const columnWidthsToUse = getColumnWidthsToUse(isMobile, isTablet);
    const [, setListSearchTerm] = useSearch(query => {
        setWishListLinesParameter({
            page: 1,
            wishListId: buyingList?.id || "",
            query,
        });
    });
    if (buyingList && wishListLinesParameter.wishListId !== buyingList.id) {
        setWishListLinesParameter({
            page: 1,
            wishListId: buyingList.id,
        });
    }
    useEffect(() => {
        if (isOpen) {
            fetchWishListLines();
        }
    }, [wishListLinesParameter, isOpen]);

    const updateWishList = async (productInfo: ProductInfo) => {
        if (!buyingList) {
            return;
        }
        dispatch(clearProduct());
        await updateBuyingLists(buyingList, { itemsToAdd: [productInfo.productId] });
        fetchWishListLines();
    };
    const fetchWishListLines = async () => {
        const wishListLines = await getWishListLines({ ...wishListLinesParameter });
        setWishListLines(wishListLines);
    };
    useEffect(() => {
        if (isOpen && selectedProductInfo) {
            updateWishList(selectedProductInfo);
        }
    }, [isOpen, selectedProductInfo]);
    useEffect(() => {
        if (!wishListLines && wishListLinesParameter.wishListId && isOpen) {
            fetchWishListLines();
        }
    }, [wishListLines, wishListLinesParameter, isOpen]);

    const buyingsListProductHeaders = [
        // eslint-disable-next-line react/jsx-key
        <Checkbox
            disabled={loading}
            onChange={() => {
                if (selectedProducts.length === wishListLines?.wishListLines?.length) {
                    setSelectedProducts([]);
                } else {
                    setSelectedProducts(wishListLines?.wishListLines?.map(line => line.id) || []);
                }
            }}
            checked={
                selectedProducts.length === wishListLines?.wishListLines?.length &&
                !!wishListLines?.wishListLines.length
            }
        />,
        translate("Product"),
        translate("Remove"),
    ];

    if (!buyingList) {
        return null;
    }

    const handleDeleteSelectedProducts = async () => {
        if (!selectedProducts.length) {
            return;
        }
        await updateBuyingLists(buyingList, { itemsToRemove: selectedProducts });
        await fetchWishListLines();
        setDeleteModalIsOpen(false);
        setSelectedProducts([]);
    };
    const handleDelete = async (productId: string) => {
        if (selectedProducts.includes(productId)) {
            setSelectedProducts(selectedProducts.filter(id => id !== productId));
        }
        await updateBuyingLists(buyingList, { itemsToRemove: [productId] });
        await fetchWishListLines();
    };

    const handlePageChange = (page: number) => {
        if (wishListLinesParameter.page !== page) {
            setWishListLinesParameter({
                ...wishListLinesParameter,
                page,
            });
            setSelectedProducts([]);
        }
    };
    const handleUpload = async (products: ProductDto[]) => {
        const productIds = products.map(product => product.id);
        await updateBuyingLists(buyingList, { itemsToAdd: productIds });
        await fetchWishListLines();
    };

    const hasItems = (wishListLines?.wishListLines?.length || 0) > 0;
    const hasMoreThanOnePage = (wishListLines?.pagination?.numberOfPages || 0) > 1;
    return (
        <>
            <StyledModal
                title={buyingList?.name}
                {...styles.modal}
                headline={`${translate("Add or Remove Products from")} ${buyingList?.name}`}
                isOpen={isOpen}
                handleClose={onClose}
            >
                <GridContainer>
                    <ProductModalSearch buyingList={buyingList} />

                    <GridItem width={[12, 12, 12, 12, 12]}>
                        {/* setting isOpen as a key so that the component remounts 
                           when the modal is opened or closed */}
                        <ManageModalUploadAccordian key={isOpen.toString()} handleUpload={handleUpload} />
                    </GridItem>

                    <GridItem width={12}>
                        <Typography variant="h4"> {translate("Products")} </Typography>
                    </GridItem>
                    <GridItem
                        css={css`
                            min-height: ${pxToRem(74)};
                        `}
                        width={[12, 12, 6, 6, 6]}
                    >
                        <SearchBox
                            placeholder={translate("Filter Products")}
                            extendedStyles={{
                                gridItem: {
                                    width: 12,
                                    css: css`
                                        padding: 0;
                                    `,
                                },
                            }}
                            title=""
                            onSearch={setListSearchTerm}
                        />
                    </GridItem>
                    {selectedProducts.length > 0 && (
                        <GridItem
                            css={css`
                                align-items: flex-end;
                            `}
                            width={[12, 12, 6, 6, 6]}
                        >
                            <FadeIn appear animate>
                                <Button onClick={() => setDeleteModalIsOpen(true)} variant="tertiary">
                                    {translate(`Remove Selected Product${selectedProducts.length > 1 ? "s" : ""}`)}
                                </Button>
                            </FadeIn>
                        </GridItem>
                    )}
                    {!wishListLines?.wishListLines || hasItems ? (
                        <>
                            <GridItem {...styles.gridItemCentered} width={12}>
                                {hasMoreThanOnePage && (
                                    <Pagination
                                        // this prop does exist its just not typed
                                        // @ts-ignore
                                        hidePerPageSelect
                                        currentPage={wishListLines?.pagination?.currentPage || 1}
                                        resultsPerPage={wishListLines?.pagination?.pageSize || 1}
                                        onChangeResultsPerPage={() => {}}
                                        resultsCount={wishListLines?.pagination?.totalItemCount || 1}
                                        resultsPerPageOptions={[10, 20, 50]}
                                        onChangePage={handlePageChange}
                                    />
                                )}
                            </GridItem>

                            <GridItem width={12}>
                                <DataTable {...styles.dataTable}>
                                    <colgroup>
                                        {columnWidthsToUse.map((width, index) => (
                                            <StyledCol key={index.toString()} width={width} />
                                        ))}
                                    </colgroup>
                                    <BuyingListTableHead
                                        buyingListHeaders={
                                            isMobile ? buyingsListProductHeaders.slice(1) : buyingsListProductHeaders
                                        }
                                    />

                                    {wishListLines?.wishListLines?.map(listLine => {
                                        const isSelected = selectedProducts.includes(listLine.id);

                                        return (
                                            <StyledRow
                                                evenRowCss={
                                                    loading
                                                        ? css`
                                                              background: white;
                                                          `
                                                        : undefined
                                                }
                                                isHighlighted={isSelected && !loading}
                                                key={listLine.id}
                                            >
                                                {!isMobile && (
                                                    <DataTableCell>
                                                        <Checkbox
                                                            disabled={loading}
                                                            color={loading ? "transparent" : undefined}
                                                            checked={isSelected}
                                                            onChange={() => {
                                                                if (isSelected) {
                                                                    setSelectedProducts(
                                                                        selectedProducts.filter(
                                                                            id => id !== listLine.id,
                                                                        ),
                                                                    );
                                                                } else {
                                                                    setSelectedProducts([
                                                                        ...selectedProducts,
                                                                        listLine.id,
                                                                    ]);
                                                                }
                                                            }}
                                                        />
                                                    </DataTableCell>
                                                )}
                                                <DataTableCell {...styles.dataTableNameCell}>
                                                    <Typography
                                                        ellipsis
                                                        css={
                                                            loading
                                                                ? css`
                                                                      width: 95%;
                                                                      display: block;
                                                                      height: 50px;
                                                                  `
                                                                : undefined
                                                        }
                                                    >
                                                        <Link
                                                            css={css`
                                                                white-space: normal;
                                                            `}
                                                            href={listLine.productUri}
                                                        >
                                                            {loading ? "" : listLine.shortDescription}
                                                        </Link>
                                                    </Typography>
                                                </DataTableCell>
                                                <DataTableCell
                                                    onClick={() => {
                                                        handleDelete(listLine.id);
                                                    }}
                                                    css={css`
                                                        padding-left: 1.5rem;
                                                    `}
                                                >
                                                    <Icon src={DeleteIcon} {...styles.deleteIcon} />
                                                </DataTableCell>
                                            </StyledRow>
                                        );
                                    })}
                                </DataTable>
                            </GridItem>
                            {selectedProducts.length > 0 && (
                                <GridItem width={12} {...styles.gridItemWarningBox}>
                                    <FadeIn animate={true} appear={true} duration={200}>
                                        <InfosBox {...styles.infosBox}>
                                            {siteMessage("UserSetup_AssignShipTo_PagerWarning")}
                                        </InfosBox>
                                    </FadeIn>
                                </GridItem>
                            )}
                        </>
                    ) : (
                        <GridItem width={12} {...styles.gridItemCentered}>
                            <Typography
                                css={css`
                                    padding-block: 1.5rem;
                                `}
                                variant="h4"
                            >
                                {translate("This List has No Products")}
                            </Typography>
                        </GridItem>
                    )}
                </GridContainer>
            </StyledModal>
            <TwoButtonModal
                headlineText={translate(`Remove Item${selectedProducts.length > 1 ? "s" : ""}`)}
                modalIsOpen={deleteModalIsOpen}
                messageText={translate(
                    `Are you sure you want to remove ${selectedProducts.length > 1 ? "these items" : "this item"}?`,
                )}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Remove")}
                onCancel={() => setDeleteModalIsOpen(false)}
                onSubmit={handleDeleteSelectedProducts}
                submitTestSelector="submitDeleteListItems"
            ></TwoButtonModal>
        </>
    );
};

export default ManageProductsModal;
